import { Helmet } from "react-helmet";
import Navigation from "@/components/Navigation";
import Breadcrumb from "@/components/Breadcrumb";
import SchemaMarkup from "@/components/SchemaMarkup";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { createFAQSchema, createOrganizationSchema } from "@/utils/schemaHelpers";

const FAQ = () => {
  const faqs = [
    {
      question: "¿Qué es un horóscopo diario?",
      answer: "El horóscopo diario es una predicción astrológica basada en la posición de los astros en un día específico. Proporciona orientación sobre amor, trabajo, salud y dinero para cada signo zodiacal. En Midstino, actualizamos nuestros horóscopos diariamente para ofrecerte las predicciones más precisas basadas en la astrología occidental y china."
    },
    {
      question: "¿Cómo funciona la compatibilidad zodiacal?",
      answer: "La compatibilidad zodiacal se basa en los elementos (fuego, tierra, aire, agua) y las características de cada signo. Los signos del mismo elemento suelen ser más compatibles, mientras que algunos signos opuestos pueden complementarse perfectamente. Nuestro test de compatibilidad analiza amor, amistad y trabajo entre dos signos para darte una visión completa de la relación."
    },
    {
      question: "¿Con qué frecuencia se actualizan los horóscopos?",
      answer: "Los horóscopos diarios se actualizan cada día con nuevas predicciones. También ofrecemos predicciones semanales y mensuales que se actualizan al inicio de cada período correspondiente. Esto te permite planificar a corto, medio y largo plazo según las energías astrales."
    },
    {
      question: "¿Qué es la tirada de 3 cartas del tarot?",
      answer: "La tirada de 3 cartas es una de las lecturas más populares del tarot. Cada carta representa un aspecto temporal: Pasado (acontecimientos que te han llevado al presente), Presente (tu situación actual y desafíos), y Futuro (el resultado probable si continúas en tu camino actual). Es una herramienta de autoconocimiento y reflexión."
    },
    {
      question: "¿Cuál es la diferencia entre el zodiaco occidental y el chino?",
      answer: "El zodiaco occidental se basa en 12 signos determinados por tu fecha de nacimiento dentro del año solar (Aries, Tauro, Géminis, etc.). El zodiaco chino también tiene 12 signos, pero se basan en tu año de nacimiento en un ciclo de 12 años (Rata, Buey, Tigre, etc.). Ambos sistemas ofrecen perspectivas únicas sobre tu personalidad y destino."
    },
    {
      question: "¿Cómo sé cuál es mi signo del zodiaco chino?",
      answer: "Tu signo del zodiaco chino se determina por tu año de nacimiento. Puedes usar nuestra calculadora de zodiaco chino en la página principal ingresando tu año de nacimiento. Ten en cuenta que el Año Nuevo chino no coincide con el 1 de enero, así que si naciste en enero o febrero, verifica la fecha exacta del Año Nuevo chino de tu año de nacimiento."
    },
    {
      question: "¿Son gratuitos todos los servicios de Midstino?",
      answer: "Sí, todos nuestros servicios son completamente gratuitos. Puedes acceder a horóscopos diarios, semanales y mensuales, realizar test de compatibilidad y obtener lecturas de tarot sin ningún costo. Nuestro objetivo es hacer la astrología accesible para todos."
    },
    {
      question: "¿Qué significa mi elemento zodiacal?",
      answer: "Los 12 signos del zodiaco se dividen en 4 elementos: Fuego (Aries, Leo, Sagitario) representa pasión y energía; Tierra (Tauro, Virgo, Capricornio) simboliza estabilidad y practicidad; Aire (Géminis, Libra, Acuario) indica comunicación e intelecto; Agua (Cáncer, Escorpio, Piscis) refleja emoción e intuición. Tu elemento influye en tu personalidad y compatibilidad."
    },
    {
      question: "¿Puedo confiar en las predicciones del horóscopo?",
      answer: "El horóscopo es una herramienta de autoconocimiento y reflexión, no una ciencia exacta. Las predicciones te ofrecen orientación basada en patrones astrológicos, pero tú siempre tienes el libre albedrío y el poder de tomar tus propias decisiones. Úsalo como una guía inspiradora, no como un mandato absoluto."
    },
    {
      question: "¿Cómo interpreto mi lectura de tarot?",
      answer: "La lectura de tarot debe interpretarse como un reflejo de tu situación interna y externa. Las cartas no predicen un futuro fijo, sino que te muestran tendencias, energías y posibilidades. Reflexiona sobre cómo las cartas resuenan con tu vida actual y usa esa información para tomar decisiones más conscientes. La intuición personal es clave en la interpretación."
    }
  ];

  const faqSchema = createFAQSchema(faqs);
  const organizationSchema = createOrganizationSchema();

  return (
    <>
      <Helmet>
        <title>Preguntas Frecuentes sobre Horóscopo y Tarot - Midstino</title>
        <meta name="description" content="Encuentra respuestas a las preguntas más comunes sobre horóscopos diarios, compatibilidad zodiacal, tarot y astrología occidental y china." />
        <meta property="og:title" content="Preguntas Frecuentes - Midstino" />
        <meta property="og:description" content="Respuestas sobre horóscopos, compatibilidad zodiacal y tarot gratis." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={`${window.location.origin}/faq`} />
      </Helmet>

      <SchemaMarkup schema={faqSchema} />
      <SchemaMarkup schema={organizationSchema} />

      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container mx-auto px-4 pt-24 pb-16">
          <Breadcrumb 
            items={[
              { name: "Inicio", url: "/" },
              { name: "Preguntas Frecuentes" }
            ]}
          />

          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-center mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Preguntas Frecuentes
            </h1>
            <p className="text-center text-xl text-muted-foreground mb-12">
              Todo lo que necesitas saber sobre horóscopos, compatibilidad y tarot
            </p>
            
            <Card className="p-8 gradient-celestial">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left text-lg font-semibold">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Card>

            <Card className="p-6 mt-8 bg-primary/10">
              <h2 className="text-xl font-bold mb-3 text-center">¿Tienes más preguntas?</h2>
              <p className="text-center text-muted-foreground">
                Si no encontraste la respuesta que buscabas, no dudes en{" "}
                <a href="/contacto" className="text-primary hover:text-primary/80 font-semibold">
                  contactarnos
                </a>
                . Estamos aquí para ayudarte.
              </p>
            </Card>
          </div>
        </main>
      </div>
    </>
  );
};

export default FAQ;
